import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Text, HStack, Icon, VStack, Collapse, Flex } from "@chakra-ui/react";
import { FaChevronDown, FaChevronRight, FaRegLightbulb, FaSearch } from "react-icons/fa";
import { FaTableList } from "react-icons/fa6";
import { ArtifactProto, ExecutionStepProto } from "build-ts/chat_session";
import { MdCheckCircle, MdErrorOutline } from "react-icons/md";
import {
	formatDurationFromDeltaMs,
	formatToolRunDuration,
	minMaxExecutionStepTimestampsMs,
} from "../../utils/helperFuncUtils";
import {
	groupExecutionStepsByToolRun,
	toolRunStreamIconKind,
	type ToolExecutionRun,
} from "../../utils/excution_steps/executionStepToolRunGroups";
import { toolRunLabelParts } from "../../utils/excution_steps/executionStepStatusLabels";
import { resolveArtifactsForSuccessfulToolRun } from "../../utils/excution_steps/executionStepToolRunArtifact";
import { LuWorkflow } from "react-icons/lu";

export interface ArtifactsListProps {
	onArtifactClick?: (artifact: ArtifactProto) => void;
	executionSteps?: ExecutionStepProto[];
	/** While the assistant message is still streaming, show Thinking vs Working instead of the final summary. */
	isStreaming?: boolean;
	/** True when assistant has started streaming reply text; triggers "Worked for" collapse and hides the live "Working/Thinking" status row. */
	hasStreamingAssistantText?: boolean;
}

const ArtifactsList: React.FC<ArtifactsListProps> = ({
	onArtifactClick,
	executionSteps,
	isStreaming = false,
	hasStreamingAssistantText = false,
}) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const wasStreamingRef = useRef(isStreaming);
	const wasRespondingPhaseRef = useRef(false);

	useEffect(() => {
		if (wasStreamingRef.current && !isStreaming) {
			setIsExpanded(false);
		}
		wasStreamingRef.current = isStreaming;
	}, [isStreaming]);

	const stepCount = executionSteps?.length ?? 0;
	const toolRuns = useMemo(() => groupExecutionStepsByToolRun(executionSteps), [executionSteps]);
	const toolRunCount = toolRuns.length;

	const hasIncompleteToolRun = useMemo(
		() => toolRuns.some(r => !r.completed && !r.failed),
		[toolRuns],
	);

	/** Assistant tokens streaming after tools finished — show final “Worked for” card collapsed, no “Responding” row. */
	const isRespondingPhase =
		isStreaming && hasStreamingAssistantText && !hasIncompleteToolRun;
	const useCompleteStyleCard = !isStreaming || isRespondingPhase;
	const showStreamingStatusRow = isStreaming && !isRespondingPhase;

	useEffect(() => {
		if (isRespondingPhase && !wasRespondingPhaseRef.current) {
			setIsExpanded(false);
		}
		wasRespondingPhaseRef.current = isRespondingPhase;
	}, [isRespondingPhase]);

	const workedDuration = useMemo(
		() => formatToolRunDuration(executionSteps ?? []),
		[executionSteps],
	);
	const completeTitlePlain = workedDuration ? `Worked for ${workedDuration}` : "Worked";
	const groupCountLabel = `${toolRunCount} ${toolRunCount === 1 ? "step" : "steps"}`;

	const streamStatusLabel = hasIncompleteToolRun ? "Working ... " : "Thinking ... ";

	type TimelineItem =
		| { kind: "run"; run: ToolExecutionRun; runIndex: number }
		| { kind: "thinking"; id: string; durationLabel: string };

	const timelineItems = useMemo((): TimelineItem[] => {
		const steps = executionSteps ?? [];
		const items: TimelineItem[] = [];
		const globalBounds = minMaxExecutionStepTimestampsMs(steps);

		for (let i = 0; i < toolRuns.length; i++) {
			const run = toolRuns[i]!;
			items.push({ kind: "run", run, runIndex: i });
			const done = run.completed || run.failed;
			if (!done) continue;

			const runBounds = minMaxExecutionStepTimestampsMs(run.steps);
			if (!runBounds) continue;

			if (i < toolRuns.length - 1) {
				const nextRun = toolRuns[i + 1]!;
				const nextBounds = minMaxExecutionStepTimestampsMs(nextRun.steps);
				if (nextBounds && nextBounds.minMs > runBounds.maxMs) {
					const deltaMs = nextBounds.minMs - runBounds.maxMs;
					const durationLabel = formatDurationFromDeltaMs(deltaMs);
					if (durationLabel) {
						items.push({ kind: "thinking", id: `think-after-${i}`, durationLabel });
					}
				}
			} else if (globalBounds && globalBounds.maxMs > runBounds.maxMs) {
				const deltaMs = globalBounds.maxMs - runBounds.maxMs;
				const durationLabel = formatDurationFromDeltaMs(deltaMs);
				if (durationLabel) {
					items.push({ kind: "thinking", id: "think-tail", durationLabel });
				}
			}
		}
		return items;
	}, [toolRuns, executionSteps]);

	if (!isStreaming && (stepCount === 0 || toolRunCount === 0)) {
		return null;
	}

	const streamingTitleSx = {
		"@keyframes reasoningShine": {
			"0%": { backgroundPosition: "120% center" },
			"100%": { backgroundPosition: "-120% center" },
		},
		display: "inline-block",
		backgroundImage:
			"linear-gradient(95deg, var(--chakra-colors-gray-700) 0%, var(--chakra-colors-gray-700) 42%, var(--chakra-colors-purple-100) 50%, var(--chakra-colors-purple-200) 51%, var(--chakra-colors-purple-300) 52%, var(--chakra-colors-gray-700) 58%, var(--chakra-colors-gray-700) 100%)",
		backgroundSize: "220% 100%",
		backgroundClip: "text",
		WebkitBackgroundClip: "text",
		WebkitTextFillColor: "transparent",
		color: "transparent",
		animation: "reasoningShine 2.4s ease-in-out infinite",
	} as const;

	const lineColor = "gray.300";
	const lineMuted = "gray.200";

	// This is the timeline of the tool runs and the thinking steps
	const timelineOfToolRunsAndThinkingSteps = (
		<VStack spacing={0} align="flex-start" px={3} py={2} w="100%">
			<VStack align="stretch" spacing={0} w="100%">
				{timelineItems.map((item, i) => {
					const prev = i > 0 ? timelineItems[i - 1] : undefined;
					const next = i < timelineItems.length - 1 ? timelineItems[i + 1] : undefined;
					const isFirst = i === 0;
					const isLast = i === timelineItems.length - 1;

					const prevRunDone =
						prev?.kind === "thinking"
							? true
							: prev?.kind === "run"
								? prev.run.completed || prev.run.failed
								: false;

					const topConnectorBg = isFirst ? "transparent" : prevRunDone ? lineColor : lineMuted;

					if (item.kind === "thinking") {
						const bottomConnectorBg =
							isLast
								? "transparent"
								: next?.kind === "run" && !next.run.completed && !next.run.failed
									? lineMuted
									: lineColor;
						return (
							<Box key={item.id} w="100%" borderRadius="md" px={1} py={0.5}>
								<HStack align="center" spacing={3} w="100%">
									<Flex
										direction="column"
										align="center"
										justify="space-between"
										w="20px"
										h="25px"
										flexShrink={0}
									>
										<Box
											w="1px"
											h="3px"
											flexShrink={0}
											borderRadius="full"
											bg={topConnectorBg}
											opacity={isFirst ? 0 : 0.9}
										/>
										<Flex
											align="center"
											justify="center"
											w="20px"
											h="19px"
											borderRadius="full"
											position="relative"
											flexShrink={0}
										>
											<Icon as={FaRegLightbulb} boxSize={4} color="gray.400" />
										</Flex>
										<Box
											w="1px"
											h="3px"
											flexShrink={0}
											borderRadius="full"
											bg={bottomConnectorBg}
											opacity={isLast ? 0 : 0.9}
										/>
									</Flex>
									<HStack
										flex={1}
										align="center"
										spacing={1}
										justify="flex-start"
										minW={0}
										minH="25px"
										flexWrap="wrap"
										rowGap={0}
									>
										<Text
											fontSize="sm"
											color="gray.400"
											fontWeight="500"
											lineHeight="1.25"
											mb={0}
											textAlign="left"
											minW={0}
											flexShrink={1}
										>
											Thought for
										</Text>
										<Text
											fontSize="sm"
											color="gray.400"
											fontWeight="500"
											mb={0}
											lineHeight="1.25"
											whiteSpace="nowrap"
											flexShrink={0}
											sx={{ fontVariantNumeric: "tabular-nums" }}
										>
											{item.durationLabel}
										</Text>
									</HStack>
								</HStack>
							</Box>
						);
					}

					const run = item.run;
					const runIndex = item.runIndex;
					const labelParts = toolRunLabelParts(run);
					const done = run.completed || run.failed;
					const kind = toolRunStreamIconKind(run);
					const StreamIcon =
						kind === "search"
							? FaSearch
							: kind === "dataframe"
								? FaTableList
								: LuWorkflow;
					const durationStr = done ? formatToolRunDuration(run.steps) : null;
					const openArtifacts = resolveArtifactsForSuccessfulToolRun(run);
					const isRunClickable = !!onArtifactClick && openArtifacts.length > 0;
					const node = run.failed ? (
						<Icon as={MdErrorOutline} boxSize={4} color="red.500" />
					) : done ? (
						<Icon as={MdCheckCircle} boxSize={4} color="green.500" />
					) : (
						<>
							<Box
								position="absolute"
								inset="-1px"
								borderRadius="full"
								pointerEvents="none"
								sx={{
									"@keyframes timelineGlow": {
										"0%, 100%": {
											opacity: 0.4,
											boxShadow: "0 0 0 0 rgba(251, 207, 232, 0.35)",
										},
										"50%": {
											opacity: 0.75,
											boxShadow: "0 0 0 3px rgba(251, 207, 232, 0.2)",
										},
									},
									animation: "timelineGlow 2.4s ease-in-out infinite",
									bg: "pink.50",
								}}
							/>
							<Icon as={StreamIcon} boxSize={4} color="gray.500" zIndex={1} />
						</>
					);
					return (
						<Box
							key={`run-${runIndex}-${run.steps[0]?.timestamp_ms ?? runIndex}`}
							w="100%"
							borderRadius="md"
							px={1}
							py={0.5}
							cursor={isRunClickable ? "pointer" : undefined}
							role={isRunClickable ? "button" : undefined}
							tabIndex={isRunClickable ? 0 : undefined}
							aria-label={isRunClickable ? `Open artifact: ${labelParts.full}` : undefined}
							onClick={
								isRunClickable
									? e => {
										e.stopPropagation();
										openArtifacts.forEach(a => onArtifactClick?.(a));
									}
									: undefined
							}
							onKeyDown={
								isRunClickable
									? e => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault();
											e.stopPropagation();
											openArtifacts.forEach(a => onArtifactClick?.(a));
										}
									}
									: undefined
							}
							_hover={isRunClickable ? { bg: "gray.100" } : undefined}
							_focusVisible={
								isRunClickable
									? { outline: "2px solid", outlineColor: "purple.300", outlineOffset: "2px" }
									: undefined
							}
						>
							<HStack align="center" spacing={3} w="100%">
								<Flex
									direction="column"
									align="center"
									justify="space-between"
									w="20px"
									h="25px"
									flexShrink={0}
								>
									<Box
										w="1px"
										h="3px"
										flexShrink={0}
										borderRadius="full"
										bg={topConnectorBg}
										opacity={isFirst ? 0 : 0.9}
									/>
									<Flex
										align="center"
										justify="center"
										w="20px"
										h="19px"
										borderRadius="full"
										position="relative"
										flexShrink={0}
									>
										{node}
									</Flex>
									<Box
										w="1px"
										h="3px"
										flexShrink={0}
										borderRadius="full"
										bg={isLast ? "transparent" : done ? lineColor : lineMuted}
										opacity={isLast ? 0 : 0.9}
									/>
								</Flex>
								<HStack
									flex={1}
									align="center"
									spacing={2}
									justify="flex-start"
									minW={0}
									minH="25px"
									flexWrap="wrap"
									rowGap={0}
								>
									{done && labelParts.artifactHighlight ? (
										<HStack
											spacing={1}
											align="center"
											minW={0}
											flexWrap="wrap"
											flexShrink={1}
										>
											<Text
												fontSize="sm"
												color={run.failed ? "red.700" : "gray.700"}
												fontWeight="500"
												lineHeight="1.25"
												mb={0}
												as="span"
											>
												{labelParts.prefix}
											</Text>
											<Icon
												as={StreamIcon}
												boxSize={4}
												color="gray.500"
												flexShrink={0}
												aria-hidden
											/>
											<Text
												fontSize="sm"
												color={run.failed ? "red.700" : "gray.900"}
												fontWeight="600"
												lineHeight="1.25"
												mb={0}
												as="span"
												minW={0}
											>
												{labelParts.artifactHighlight}
											</Text>
										</HStack>
									) : (
										<Text
											fontSize="sm"
											color={run.failed ? "red.700" : "gray.700"}
											fontWeight="500"
											lineHeight="1.25"
											mb={0}
											textAlign="left"
											minW={0}
											flexShrink={1}
										>
											{labelParts.full}
										</Text>
									)}
									{durationStr ? (
										<Text
											fontSize="sm"
											color="gray.400"
											fontWeight="500"
											mb={0}
											lineHeight="1.25"
											whiteSpace="nowrap"
											flexShrink={0}
											sx={{ fontVariantNumeric: "tabular-nums" }}
										>
											{durationStr}
										</Text>
									) : null}
								</HStack>
							</HStack>
						</Box>
					);
				})}
			</VStack>
		</VStack>
	);

	return (
		<VStack
			w="100%"
			maxW="100%"
			minW={0}
			alignSelf="stretch"
			align="stretch"
			spacing={2}
			m={0}
		>
			{showStreamingStatusRow ? (
				<Box w="100%" textAlign="left">
					<Text
						as="span"
						fontSize="md"
						fontWeight="500"
						lineHeight="short"
						mb={0}
						sx={streamingTitleSx}
					>
						{streamStatusLabel}
					</Text>
				</Box>
			) : null}

			{toolRunCount > 0 ? (
				<Box
					bg="white"
					borderRadius="md"
					w="100%"
					maxW="100%"
					minW={0}
					overflow="hidden"
					border="1px solid"
					borderColor="gray.300"
				>
					{useCompleteStyleCard ? (
						<>
							<Flex
								w="100%"
								align="center"
								justify="space-between"
								gap={2}
								p={2}
								px={3}
								cursor="pointer"
								onClick={() => setIsExpanded(!isExpanded)}
								_hover={{ bg: "gray.100" }}
								transition="background 0.2s"
							>
								<Box flex="1" minW={0} textAlign="left">
									{/* When Expanded it will show the"Worked for X seconds" */}
									{/* When Collapsed it will show the "Worked for X seconds (X steps)" */}
									{isExpanded ? (
										<Text fontSize="sm" color="gray.700" fontWeight="600" mb={0} textAlign="left">
											{completeTitlePlain}
										</Text>
									) : (
										<HStack
											as="span"
											display="inline-flex"
											spacing={1}
											align="baseline"
											flexWrap="wrap"
											mb={0}
										>
											<Text as="span" fontSize="sm" color="gray.700" fontWeight="600">
												{completeTitlePlain}
											</Text>
											<Text as="span" fontSize="sm" color="gray.500" fontWeight="500">
												({groupCountLabel})
											</Text>
										</HStack>
									)}
								</Box>
								<Icon
									as={isExpanded ? FaChevronDown : FaChevronRight}
									boxSize={3}
									color="gray.600"
									flexShrink={0}
								/>
							</Flex>
							<Collapse in={isExpanded} animateOpacity>
								{timelineOfToolRunsAndThinkingSteps}
							</Collapse>
						</>
					) : (
						timelineOfToolRunsAndThinkingSteps
					)}
				</Box>
			) : null}
		</VStack>
	);
};

export default ArtifactsList;
